import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import logoSrc from "../letsloop-logo.png";
import { CookiePreferencesLink } from "./ConsentPreferences";
import AppDownload from "./AppDownload";
import {
  LOGIN_LINK,
  SIGNUP_LINK,
  WHATSAPP_COMMUNITY_URL,
  type AccountLinkTarget,
} from "../lib/appLinks";

// LetsLoop site footer.
//
// Structure mirrors the Meetup footer the operator referenced (LL-0093):
// a compact top band (wordmark + tagline left, primary CTA right, divider),
// a four-column link grid (Your account · Discover · LetsLoop · Follow us +
// Get the app), and a bottom legal strip. Styling stays on the LetsLoop warm
// editorial palette (paper-tan / ink / salmon), not Meetup's dark card, so it
// belongs to the rest of the single light-theme site.
//
// Every link points at a route/URL that actually exists today. Account links
// flip to the desktop PWA (app.letsloop.app) the moment WEB_APP_LIVE is set in
// lib/appLinks.ts; until then they route to /download (create your account in
// the app). No dead links ship.

const CONTACT_EMAIL = "hello@letsloop.app";

// CSS variable (not a literal) so inline `color: INK` usages flip with the theme.
const INK = "var(--color-ink)";
const MUTED = "var(--color-muted)";

interface FooterProps {
  onNavigate?: (page: string) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const navigate = useNavigate();

  const goToSection = (id: string) => {
    if (onNavigate) {
      onNavigate(id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <footer
      className="relative px-6 md:px-12 pt-16 pb-12"
      style={{ backgroundColor: "var(--color-paper-tan)", color: INK }}
      aria-labelledby="footer-heading"
    >
      <h2 id="footer-heading" className="sr-only">
        LetsLoop footer
      </h2>

      <div className="max-w-[1400px] mx-auto">
        {/* ── Brand wordmark hero ────────────────────────────────────
            Big LetsLoop logo with the two high-five characters overlaid on
            the upper-middle of the wordmark (loby reference). Sits above the
            functional footer band. */}
        <div
          className="relative flex items-end justify-center mb-6 md:mb-10"
          style={{ minHeight: "500px" }}
        >
          {/* translateY lifts the logo upward without moving the
              absolutely-positioned cartoon, which is anchored to the
              wrapper, not the logo itself. */}
          <img
            src={logoSrc}
            alt="LetsLoop"
            width={2537}
            height={567}
            loading="lazy"
            decoding="async"
            className="w-full max-w-[1300px] h-auto select-none relative z-0"
            draggable={false}
            style={{ transform: "translateY(-180px)" }}
          />
          {/* High-five cartoon — overlaps the upper-middle of the logo. */}
          <div
            className="absolute z-10 flex items-center justify-center pointer-events-none"
            style={{
              top: "8%",
              left: "50%",
              transform: "translateX(-50%)",
              width: "30%",
            }}
          >
            <HighFiveCartoon />
          </div>
        </div>

        {/* ── Top band: tagline · primary CTA ─────────────────────── */}
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between pb-8">
          <span
            className="text-base md:text-lg font-medium"
            style={{ color: "var(--color-body)" }}
          >
            Something&rsquo;s always happening near you.
          </span>

          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-base font-semibold">
              Start something in your city.
            </span>
            <a
              href={WHATSAPP_COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-1.5 font-bold text-salmon transition-colors hover:text-salmon-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon rounded"
            >
              Join the community
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </a>
          </div>
        </div>

        <div
          className="h-px w-full"
          style={{ backgroundColor: "var(--color-hairline)" }}
        />

        {/* ── Link grid ──────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-12 pt-12">
          {/* Your account */}
          <FooterColumn title="Your account">
            <AccountLink label="Log in" target={LOGIN_LINK} />
            <AccountLink label="Sign up" target={SIGNUP_LINK} />
            <FooterExternal label="Help" href={`mailto:${CONTACT_EMAIL}`} />
          </FooterColumn>

          {/* Discover */}
          <FooterColumn title="Discover">
            <FooterInternal label="Communities" to="/communities" />
            <FooterInternal label="How it works" to="/how-it-works" />
            <FooterInternal label="News" to="/news" />
          </FooterColumn>

          {/* LetsLoop */}
          <FooterColumn title="LetsLoop">
            <FooterButton
              label="Mission"
              onClick={() => goToSection("mission")}
            />
            <FooterInternal label="Safety" to="/safety" />
            <FooterInternal label="Support" to="/support" />
          </FooterColumn>

          {/* Follow us + Get the app */}
          <div className="space-y-8">
            <div className="space-y-4">
              <ColumnHeading>Follow us</ColumnHeading>
              <div className="flex gap-2.5">
                <SocialChip
                  href="https://www.instagram.com/letsloop.app"
                  label="LetsLoop on Instagram"
                >
                  <InstagramGlyph />
                </SocialChip>
                {/* /company/letsloop 404s. The real page is /letsloopapp,
                    checked on 2026-08-04 against a control that returned 200,
                    so this is a dead link and not LinkedIn blocking a bot. It
                    is also the URL the entity graph claims in `sameAs`, and a
                    sameAs pointing at a 404 is worse than none: it asks a
                    consumer to merge us with nothing. */}
                <SocialChip
                  href="https://www.linkedin.com/company/letsloopapp"
                  label="LetsLoop on LinkedIn"
                >
                  <LinkedInGlyph />
                </SocialChip>
              </div>
            </div>

            <div className="space-y-4">
              <ColumnHeading>Get the app</ColumnHeading>
              <AppDownload placement="footer" />
            </div>
          </div>
        </div>

        {/* ── Legal strip ────────────────────────────────────────── */}
        <div
          className="mt-16 pt-6 border-t flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
          style={{ borderColor: "var(--color-hairline)" }}
        >
          {/* Copyright + the statutory trading disclosure stack together, so
              the legal strip keeps exactly TWO flex children and the md:row
              layout is unchanged. A third sibling would have become a third
              column. */}
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium" style={{ color: MUTED }}>
              &copy; 2026 LetsLoop Ltd.
            </p>
            {/* Required by the Companies (Trading Disclosures) Regulations,
                made under Companies Act 2006 s.82: a company's websites must
                state its registered name, registered number, place of
                registration and registered office address. None of the four
                was published anywhere on this site before 2026-08-11.
                Verified against the register that day: LETSLOOP LTD, 17178512,
                incorporated 24 April 2026. Do not edit these without
                re-checking Companies House. */}
            <p className="text-xs" style={{ color: MUTED }}>
              LetsLoop is a trading name of LETSLOOP LTD, registered in England
              and Wales, company number 17178512. Registered office: 71-75
              Shelton Street, Covent Garden, London WC2H 9JQ.
            </p>
          </div>
          <nav
            aria-label="Legal"
            className="flex flex-wrap items-center gap-x-5 gap-y-2"
          >
            <LegalLink to="/terms">Terms of service</LegalLink>
            <LegalLink to="/privacy">Privacy policy</LegalLink>
            <LegalLink to="/cookies">Cookie policy</LegalLink>
            <CookiePreferencesLink className="text-sm font-medium text-ink/45 transition-colors hover:text-salmon focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon rounded">
              Cookie preferences
            </CookiePreferencesLink>
            <LegalLink to="/safety">Safety</LegalLink>
            <LegalLink to="/support">Support</LegalLink>
            <LegalLink to="/delete-account">Delete account</LegalLink>
          </nav>
        </div>

        <p className="mt-6 text-sm font-medium" style={{ color: MUTED }}>
          Made with{" "}
          <span aria-hidden="true" className="text-salmon">
            &hearts;
          </span>{" "}
          in London.
        </p>
      </div>
    </footer>
  );
};

// ── Column primitives ──────────────────────────────────────────────
function ColumnHeading({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="text-sm font-bold uppercase tracking-widest"
      style={{ color: MUTED }}
    >
      {children}
    </div>
  );
}

function FooterColumn({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4">
      <ColumnHeading>{title}</ColumnHeading>
      <ul className="space-y-3">{children}</ul>
    </div>
  );
}

// text-ink, NOT a pinned hex. These sit on the footer's themed ground
// (paper-tan, which flips to #20242e in dark), so a fixed #2d3849 was dark ink
// on a dark ground: the whole footer nav measured 1.31:1 in dark mode.
const linkClass =
  "text-sm font-semibold text-left text-ink transition-colors hover:text-salmon focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon rounded";

function FooterInternal({ label, to }: { label: string; to: string }) {
  return (
    <li>
      <Link to={to} className={linkClass}>
        {label}
      </Link>
    </li>
  );
}

function FooterButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <li>
      <button onClick={onClick} className={linkClass}>
        {label}
      </button>
    </li>
  );
}

function FooterExternal({ label, href }: { label: string; href: string }) {
  // mailto:/tel: links should not open a new tab (some browsers leave an empty
  // one); only http(s) links get the new-tab + noopener treatment.
  const newTab = /^https?:/i.test(href);
  return (
    <li>
      <a
        href={href}
        className={linkClass}
        style={{ color: INK }}
        {...(newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {label}
      </a>
    </li>
  );
}

// Account links flip between the desktop PWA (external, when live) and the
// internal /download route (today). lib/appLinks.ts owns which; this renders on
// the resolved shape, so the flag lives in exactly one place.
function AccountLink({
  label,
  target,
}: {
  label: string;
  target: AccountLinkTarget;
}) {
  return "href" in target ? (
    <FooterExternal label={label} href={target.href} />
  ) : (
    <FooterInternal label={label} to={target.to} />
  );
}

function LegalLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="text-sm font-medium transition-colors hover:text-salmon focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon rounded"
      style={{ color: MUTED }}
    >
      {children}
    </Link>
  );
}

// ── Social chips ───────────────────────────────────────────────────
function SocialChip({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-salmon"
      style={{ backgroundColor: "#FFA07A" }}
    >
      {children}
    </a>
  );
}

function InstagramGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="20" height="20" x="2" y="2" rx="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

function LinkedInGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

// ── High-five cartoon ──────────────────────────────────────────────
// Two friendly stylised characters mid-high-five. Line-art with the
// brand pastels (coral + teal-mint). Sits over the wordmark to add
// warmth without needing a custom illustration commission.
function HighFiveCartoon() {
  return (
    <svg
      viewBox="0 0 400 360"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto"
    >
      {/* Left character (seated, coral top, laptop) */}
      <g>
        {/* Body / lap */}
        <path
          d="M40 280 Q 30 220 80 200 L 160 200 Q 200 210 200 260 L 200 320 L 40 320 Z"
          fill="#FFC8AC"
          stroke="#2D3849"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        {/* Laptop */}
        <rect
          x="90"
          y="240"
          width="100"
          height="56"
          rx="4"
          fill="#FFFFFF"
          stroke="#2D3849"
          strokeWidth="3"
        />
        <circle
          cx="140"
          cy="268"
          r="6"
          fill="#FFA07A"
          stroke="#2D3849"
          strokeWidth="2"
        />
        {/* Head */}
        <circle
          cx="120"
          cy="150"
          r="42"
          fill="#FFFFFF"
          stroke="#2D3849"
          strokeWidth="3"
        />
        {/* Hair bun */}
        <circle cx="110" cy="106" r="14" fill="#2D3849" />
        <path
          d="M82 150 Q 86 124 110 116"
          stroke="#2D3849"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />
        {/* Smile + eyes (eyes closed = happy) */}
        <path
          d="M108 150 Q 112 154 116 150"
          stroke="#2D3849"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M126 150 Q 130 154 134 150"
          stroke="#2D3849"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M114 168 Q 122 174 130 168"
          stroke="#2D3849"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        {/* Cheek dot */}
        <circle cx="100" cy="166" r="4" fill="#FFA07A" />
        {/* Raised arm — meets the right character's hand */}
        <path
          d="M160 220 Q 200 160 245 130"
          stroke="#2D3849"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M156 230 Q 200 170 246 138"
          stroke="#FFC8AC"
          strokeWidth="22"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M156 230 Q 200 170 246 138"
          stroke="#2D3849"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />
        {/* Hand */}
        <circle
          cx="250"
          cy="130"
          r="18"
          fill="#FFFFFF"
          stroke="#2D3849"
          strokeWidth="3"
        />
      </g>

      {/* Right character (standing, mint top, phone) */}
      <g>
        {/* Body */}
        <path
          d="M260 320 L 260 220 Q 260 190 300 188 L 350 188 Q 380 192 380 230 L 380 320 Z"
          fill="#B7E5E0"
          stroke="#2D3849"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        {/* Head */}
        <circle
          cx="330"
          cy="150"
          r="42"
          fill="#FFFFFF"
          stroke="#2D3849"
          strokeWidth="3"
        />
        {/* Spiky hair */}
        <path
          d="M298 130 L 300 110 L 312 124 L 318 102 L 330 122 L 342 102 L 350 124 L 360 110 L 362 130"
          fill="#2D3849"
          stroke="#2D3849"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        {/* Eyes + smile */}
        <path
          d="M316 150 Q 320 154 324 150"
          stroke="#2D3849"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M336 150 Q 340 154 344 150"
          stroke="#2D3849"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M322 168 Q 330 178 338 168"
          stroke="#2D3849"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="354" cy="166" r="4" fill="#FFA07A" />
        {/* Raised arm meeting hand */}
        <path
          d="M300 218 Q 270 170 256 138"
          stroke="#B7E5E0"
          strokeWidth="22"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M300 218 Q 270 170 256 138"
          stroke="#2D3849"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />
        {/* Hand (overlaps left hand at the high-five) */}
        <circle
          cx="254"
          cy="132"
          r="18"
          fill="#FFFFFF"
          stroke="#2D3849"
          strokeWidth="3"
        />
        {/* Phone in other hand */}
        <rect x="350" y="248" width="22" height="36" rx="4" fill="#2D3849" />
      </g>

      {/* Spark accents at the high-five contact point */}
      <g stroke="#FFA07A" strokeWidth="3" strokeLinecap="round">
        <line x1="216" y1="96" x2="220" y2="80" />
        <line x1="240" y1="92" x2="248" y2="78" />
        <line x1="270" y1="92" x2="278" y2="80" />
        <line x1="288" y1="100" x2="296" y2="88" />
      </g>
    </svg>
  );
}

export default Footer;
